import { useMemo, useState } from "react";
import { Card, DataTable, Badge, Button, Text, Link, Tooltip, EmptyState, BlockStack, InlineStack } from "@shopify/polaris";
import { formatToCurrency } from "../../../shared/components/functions";
import { buildWorkbookXml, downloadWorkbook, excelCell, excelRow } from "../../../shared/components/excelExport";

const dateOf = (value) => (value ? String(value).substring(0, 10) : "");

const buildSupplierGroups = (suggestions) => {
  const groups = new Map();
  suggestions.forEach((suggestion) => {
    const key = suggestion.SupplierId || 0;
    if (!groups.has(key)) {
      groups.set(key, {
        supplierId: suggestion.SupplierId,
        supplierName: suggestion.supplierName || "No supplier",
        supplierNumber: suggestion.supplierNumber || "",
        lines: [],
        totalValue: 0,
        lateCount: 0,
      });
    }
    const group = groups.get(key);
    group.lines.push(suggestion);
    group.totalValue += Number(suggestion.LineValue) || 0;
    if (suggestion.IsLate) group.lateCount += 1;
  });
  return [...groups.values()].sort((a, b) => a.supplierName.localeCompare(b.supplierName));
};

const buildGroupRowsXml = (group) => {
  const rows = [
    excelRow([excelCell(`${group.supplierName} ${group.supplierNumber ? `(${group.supplierNumber})` : ""}`, "Title")]),
    excelRow([excelCell(`Order proposal — generated ${new Date().toISOString().substring(0, 10)}`)]),
    "<Row />",
    excelRow([
      excelCell("Part ID", "Header"),
      excelCell("Reference", "Header"),
      excelCell("Description", "Header"),
      excelCell("Qty to order", "Header"),
      excelCell("Order by", "Header"),
      excelCell("Needed on", "Header"),
      excelCell("Unit price", "Header"),
      excelCell("Line value", "Header"),
      excelCell("Type", "Header"),
    ]),
  ];
  group.lines.forEach((line) => {
    rows.push(
      excelRow([
        excelCell(line.ProductId, undefined, true),
        excelCell(line.Productref || ""),
        excelCell(line.ProductNameNL || ""),
        excelCell(line.SuggestedQty, undefined, true),
        excelCell(dateOf(line.OrderByDate), line.IsLate ? "Late" : undefined),
        excelCell(dateOf(line.NeedDate)),
        excelCell(line.UnitPrice, undefined, true),
        excelCell(line.LineValue, undefined, true),
        excelCell(line.SuggestionType),
      ])
    );
  });
  rows.push("<Row />");
  rows.push(excelRow([excelCell("Total", "Group"), excelCell(""), excelCell(""), excelCell(""), excelCell(""), excelCell(""), excelCell(""), excelCell(group.totalValue, "Group", true), excelCell("")]));
  return rows.join("");
};

const exportGroup = (group) => {
  const xml = buildWorkbookXml([{ name: group.supplierName.substring(0, 28) || "Supplier", rowsXml: buildGroupRowsXml(group) }]);
  downloadWorkbook(xml, `order-proposal-${group.supplierName.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}-${new Date().toISOString().substring(0, 10)}.xls`);
};

const exportAll = (groups) => {
  const xml = buildWorkbookXml(groups.map((group) => ({ name: (group.supplierName || "Supplier").substring(0, 28), rowsXml: buildGroupRowsXml(group) })));
  downloadWorkbook(xml, `mrp-buy-list-${new Date().toISOString().substring(0, 10)}.xls`);
};

const copyProposal = async (group, setToast) => {
  const lines = group.lines.map((line) => `${line.ProductId}\t${line.Productref || ""}\t${line.ProductNameNL || ""}\t${line.SuggestedQty}\torder by ${dateOf(line.OrderByDate)}`);
  const text = `Order proposal ${group.supplierName}\n${lines.join("\n")}`;
  try {
    await navigator.clipboard.writeText(text);
    setToast("ok");
  } catch (err) {
    setToast("error");
  }
};

const peggingTooltip = (pegging) => {
  if (!Array.isArray(pegging) || pegging.length === 0) return null;
  return pegging.map((peg, index) => `${peg.ref}${peg.qty ? ` ×${peg.qty}` : ""}`).join(" · ");
};

export function BuyList({ suggestions, setToast }) {
  const [typeFilter, setTypeFilter] = useState("ALL");
  const groups = useMemo(
    () => buildSupplierGroups(suggestions.filter((suggestion) => typeFilter === "ALL" || suggestion.SuggestionType === typeFilter)),
    [suggestions, typeFilter]
  );

  if (!suggestions.length) {
    return (
      <Card>
        <EmptyState heading="Nothing to order" image="">
          <p>No net requirements in the planning horizon. Stock plus confirmed inbound covers all demand.</p>
        </EmptyState>
      </Card>
    );
  }

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" blockAlign="center">
        <InlineStack gap="200">
          <Button pressed={typeFilter === "ALL"} onClick={() => setTypeFilter("ALL")}>
            All
          </Button>
          <Button pressed={typeFilter === "MRP"} onClick={() => setTypeFilter("MRP")}>
            Time-phased
          </Button>
          <Button pressed={typeFilter === "REORDER_POINT"} onClick={() => setTypeFilter("REORDER_POINT")}>
            Reorder point
          </Button>
        </InlineStack>
        <Button onClick={() => exportAll(groups)}>Export all (Excel)</Button>
      </InlineStack>

      {groups.map((group) => (
        <Card key={group.supplierId || "none"}>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <InlineStack gap="200" blockAlign="center">
                <Text variant="headingMd" as="h2">
                  {group.supplierId ? (
                    <Link removeUnderline url={`/companies/${group.supplierId}`}>
                      {group.supplierName}
                    </Link>
                  ) : (
                    group.supplierName
                  )}
                </Text>
                <Badge>{`${group.lines.length} part${group.lines.length === 1 ? "" : "s"}`}</Badge>
                {group.lateCount > 0 && <Badge tone="critical">{`${group.lateCount} late`}</Badge>}
                <Badge tone="info">{formatToCurrency(group.totalValue)}</Badge>
              </InlineStack>
              <InlineStack gap="200">
                <Button size="slim" onClick={() => copyProposal(group, setToast)}>
                  Copy proposal
                </Button>
                <Button size="slim" onClick={() => exportGroup(group)}>
                  Export Excel
                </Button>
              </InlineStack>
            </InlineStack>

            <DataTable
              verticalAlign="middle"
              increasedTableDensity
              columnContentTypes={["text", "text", "text", "numeric", "numeric", "text", "text", "numeric", "numeric", "text"]}
              headings={[<b>Part</b>, <b>Reference</b>, <b>Description</b>, <b>Net req.</b>, <b>Order qty</b>, <b>Order by</b>, <b>Needed on</b>, <b>Unit price</b>, <b>Value</b>, <b>Driven by</b>]}
              rows={group.lines.map((line) => {
                const tooltip = peggingTooltip(line.Pegging);
                return [
                  <Link removeUnderline url={`/products/${line.ProductId}`}>
                    {line.ProductId}
                  </Link>,
                  line.Productref || "",
                  String(line.ProductNameNL || "").substring(0, 40),
                  line.NetRequirementQty,
                  <InlineStack gap="100" blockAlign="center">
                    <Text as="span" fontWeight="bold">
                      {line.SuggestedQty}
                    </Text>
                    {Number(line.SuggestedQty) > Number(line.NetRequirementQty) && (
                      <Tooltip content="Rounded up by minimum order qty / order multiple">
                        <Badge size="small">lot</Badge>
                      </Tooltip>
                    )}
                  </InlineStack>,
                  line.IsLate ? (
                    <InlineStack gap="100" blockAlign="center">
                      {dateOf(line.OrderByDate)}
                      <Badge tone="critical">Late</Badge>
                    </InlineStack>
                  ) : (
                    dateOf(line.OrderByDate)
                  ),
                  dateOf(line.NeedDate),
                  line.UnitPrice ? formatToCurrency(line.UnitPrice) : "",
                  line.LineValue ? formatToCurrency(line.LineValue) : "",
                  tooltip ? (
                    <Tooltip content={tooltip}>
                      <Badge size="small" tone={line.SuggestionType === "REORDER_POINT" ? "attention" : "info"}>
                        {line.SuggestionType === "REORDER_POINT" ? "ROP" : `${(line.Pegging || []).length} src`}
                      </Badge>
                    </Tooltip>
                  ) : (
                    ""
                  ),
                ];
              })}
            />
          </BlockStack>
        </Card>
      ))}
    </BlockStack>
  );
}
